import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { Button } from "@/components/ui/button";
import { MapPin, Clock } from "lucide-react";
import AboutValues from "./about/AboutValues";
import AboutTeam from "./about/AboutTeam";

const AboutPage = () => {
  const { t } = useTranslation();
  const storyRef = useScrollReveal();
  const valuesRef = useScrollReveal();
  const teamRef = useScrollReveal();
  const ctaRef = useScrollReveal();

  return (
    <div data-testid="about-page" className="min-h-screen bg-[#F4F1DE]">
      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#3D405B] relative overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 opacity-10 pattern-bg"></div>
        <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
          <span className="inline-block bg-[#F2CC8F]/20 text-[#F2CC8F] px-4 py-2 rounded-full text-sm font-medium mb-6">
            {t("about.hero.badge")}
          </span>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 font-['Playfair_Display']">
            {t("about.hero.title")}
          </h1>
          <p className="text-xl text-[#F4F1DE]/80 max-w-2xl mx-auto">
            {t("about.hero.subtitle")}
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-20" data-testid="story-section" ref={storyRef}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 reveal-left">
              <span className="text-[#E07A5F] font-medium tracking-wider uppercase text-sm">
                {t("about.story.badge")}
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-[#3D405B] font-['Playfair_Display']">
                {t("about.story.title")}
              </h2>
              <p className="text-[#6B705C] text-lg leading-relaxed">{t("about.story.p1")}</p>
              <p className="text-[#6B705C] text-lg leading-relaxed">{t("about.story.p2")}</p>
              <div className="flex gap-8 pt-4">
                <div>
                  <span className="text-4xl font-bold text-[#E07A5F]">30+</span>
                  <p className="text-[#6B705C]">{t("about.story.eventsCatered")}</p>
                </div>
                <div>
                  <span className="text-4xl font-bold text-[#E07A5F]">3,000+</span>
                  <p className="text-[#6B705C]">{t("about.story.happyGuests")}</p>
                </div>
              </div>
            </div>
            <div className="relative reveal-right">
              <img
                src="https://images.unsplash.com/photo-1665332195309-9d75071138f0?w=800"
                alt="Traditional cooking"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#E07A5F] text-white p-6 rounded-2xl shadow-xl">
                <img src="/logo.svg" alt="Djohomi Foods" className="w-10 h-10 object-contain mb-2" />
                <p className="font-semibold">{t("about.story.craftedWithLove")}</p>
                <p className="text-sm text-white/80">{t("about.story.since")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <AboutValues sectionRef={valuesRef} />
      <AboutTeam sectionRef={teamRef} />

      {/* Location */}
      <section className="py-20 bg-[#3D405B]" data-testid="location-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#F2CC8F] font-medium tracking-wider uppercase text-sm">
                {t("about.location.badge")}
              </span>
              <h2 className="text-4xl font-bold text-white mt-3 mb-6 font-['Playfair_Display']">
                {t("about.location.title")}
              </h2>
              <p className="text-[#F4F1DE]/80 text-lg mb-8">{t("about.location.subtitle")}</p>
              <div className="space-y-4">
                <div className="flex items-center gap-4 text-[#F4F1DE]">
                  <MapPin className="w-6 h-6 text-[#F2CC8F]" />
                  <span>{t("about.location.address")}</span>
                </div>
                <div className="flex items-center gap-4 text-[#F4F1DE]">
                  <Clock className="w-6 h-6 text-[#F2CC8F]" />
                  <span>{t("about.location.hours")}</span>
                </div>
              </div>
              <Link to="/contact">
                <Button className="mt-8 bg-[#E07A5F] hover:bg-[#D0694E] text-white px-8 py-6 rounded-full" data-testid="schedule-visit-btn">
                  {t("about.location.btn")}
                </Button>
              </Link>
            </div>
            <div className="bg-[#F4F1DE] rounded-2xl p-8">
              <img src="https://images.unsplash.com/photo-1698934641776-49267ace4603?w=800" alt="Our kitchen" className="rounded-xl shadow-lg" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#E07A5F]" data-testid="about-cta" ref={ctaRef}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="reveal text-4xl font-bold text-white mb-4 font-['Playfair_Display']">{t("about.cta.title")}</h2>
          <p className="reveal stagger-1 text-white/90 text-lg mb-8 max-w-xl mx-auto">{t("about.cta.subtitle")}</p>
          <div className="reveal stagger-2">
            <Link to="/booking">
              <Button
                className="bg-white text-[#E07A5F] hover:bg-[#F4F1DE] text-lg px-10 py-6 rounded-full font-medium btn-hover-pulse"
                data-testid="about-cta-btn"
              >
                {t("about.cta.btn")}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
